// The side panel that inspects one file (Task Detail, Library). It is not an
// overlay: while it is open the page keeps a column for it (the page adds
// `u-page--panel`, see usePanelPage), so the grid stays usable beside it and a
// click on another tile simply shows that tile here. It slides in, and it
// slides out the same way -- Close, Esc and the parent's own close all wait
// for the animation before the panel unmounts.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

/** How long the slide takes; matches `u-slide-in`/`u-slide-out` in user.css. */
export const DRAWER_MS = 200;

export default function Drawer({
  open,
  title,
  label,
  onClose,
  children,
}: {
  open: boolean;
  title: ReactNode;
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  // Keep the last content on screen while the panel slides out.
  const last = useRef<{ title: ReactNode; children: ReactNode }>({ title, children });
  if (open) last.current = { title, children };
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;
    setClosing(true);
    const t = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, DRAWER_MS);
    return () => window.clearTimeout(t);
  }, [open, mounted]);

  const requestClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") requestClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, requestClose]);

  // The panel starts where the masthead ends (its height changes with the
  // width), and reaches the top once the masthead has scrolled away.
  const [top, setTop] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (!mounted) return;
    const place = () => {
      const mast = document.querySelector(".u-mast");
      setTop(mast ? Math.max(0, mast.getBoundingClientRect().bottom) : undefined);
    };
    place();
    window.addEventListener("scroll", place, { passive: true });
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place);
      window.removeEventListener("resize", place);
    };
  }, [mounted]);

  if (!mounted) return null;
  return (
    <aside
      style={top !== undefined ? { top } : undefined}
      className={closing ? "u-drawer u-drawer--closing" : "u-drawer"}
      role="dialog"
      aria-label={label}
      data-testid="drawer"
    >
      <div className="u-drawer__head">
        <h2 className="u-h2">{last.current.title}</h2>
        <button className="u-btn u-btn--quiet" onClick={requestClose}>
          Close
        </button>
      </div>
      {last.current.children}
    </aside>
  );
}

/** The page's class while a panel is open (or sliding out), so its content
 * leaves room for the panel instead of sitting under it. */
export function panelPageClass(base: string, open: boolean): string {
  return open ? `${base} u-page--panel` : base;
}
